import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import Skeleton from "@/components/jea/Skeleton";
import JeaButton from "@/components/jea/JeaButton";
import { demoClient, useDemo } from "@/pages/demo/DemoShell";
import { formatMoney, formatDate } from "@/lib/pricing";

const DemoDebtorProfilePage = () => {
  const { id } = useParams();
  const [debtor, setDebtor] = useState(null);
  const [loading, setLoading] = useState(true);
  const demo = useDemo();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await demoClient.get(`/demo/debtors/${id}`);
        setDebtor(data);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <Skeleton className="h-96" />;
  if (!debtor) return <div className="text-text-secondary">Debtor not found.</div>;

  const j = debtor.judgment || {};
  const interest = debtor.interest || {};

  return (
    <div className="space-y-6" data-testid="demo-debtor-profile">
      <Link to="/demo/debtors" className="inline-flex items-center gap-1 text-text-secondary hover:text-accent-pink text-[13px]">
        <ArrowLeft size={14} /> Back to Debtors
      </Link>

      <JeaCard data-tour="debtor-profile">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[24px] font-bold text-white">{debtor.full_name}</h1>
            <div className="text-text-secondary text-[13px] jea-mono mt-1">Case {j.case_number}</div>
          </div>
          <div className="flex items-center gap-2">
            <JeaButton variant="outline-pink" onClick={() => demo?.openSignUp("running queries")} data-testid="demo-profile-run">Run Query</JeaButton>
            <JeaButton onClick={() => demo?.openSignUp("editing debtors")} data-testid="demo-profile-edit">Edit</JeaButton>
          </div>
        </div>
      </JeaCard>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <JeaCard>
          <div className="jea-caption">Judgment Amount</div>
          <div className="jea-mono text-accent-pink font-extrabold text-[24px]">{formatMoney(j.amount_cents)}</div>
        </JeaCard>
        <JeaCard>
          <div className="jea-caption">Accrued Interest</div>
          <div className="jea-mono text-success font-bold text-[24px]">{formatMoney(interest.accrued_cents || 0)}</div>
        </JeaCard>
        <JeaCard>
          <div className="jea-caption">Current Total Value</div>
          <div className="jea-mono text-accent-pink font-extrabold text-[24px]">{formatMoney(interest.current_total_cents || 0)}</div>
        </JeaCard>
      </div>

      <JeaCard>
        <h2 className="text-white font-bold text-[16px] mb-3">Judgment Details</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <div className="jea-caption">Court</div>
            <div className="text-white text-[14px]">{j.court_name || "—"}</div>
          </div>
          <div>
            <div className="jea-caption">State</div>
            <div className="jea-mono text-text-secondary">{j.state}</div>
          </div>
          <div>
            <div className="jea-caption">Nature of Debt</div>
            <div className="text-white text-[14px]">{j.nature_of_debt || "—"}</div>
          </div>
          <div>
            <div className="jea-caption">Entered</div>
            <div className="jea-date">{formatDate(j.date_entered)}</div>
          </div>
          <div>
            <div className="jea-caption">Interest Rate</div>
            <div className="text-white text-[14px]">{j.interest_rate}%</div>
          </div>
          <div>
            <div className="jea-caption">Tier</div>
            <div className="text-white text-[14px] uppercase font-bold tracking-[0.08em]">{debtor.tier}</div>
          </div>
        </div>
      </JeaCard>
    </div>
  );
};

export default DemoDebtorProfilePage;
